import {
  Heading,
  Link,
  Placeholder,
  PromoCard,
  PromoCardSplit,
  Slider,
  useData,
} from '@/components';
import { TEMPLATE_BASE_PATH } from '@/constants';
import { useSpecialsContext } from '@/context';
import {
  PromoCarouselDocumentDataCarouselItemsItemWithID,
  PromoCarouselSlice,
} from '@/types';
import { getSlicePadding, getSpecialURL } from '@/utils';
import classNames from 'classnames';
import { CSSProperties, FC } from 'react';
import styles from './specialsslider.module.scss';
interface IPromoCarousel {
  slice?: PromoCarouselSlice;
}

const PromoCarousel: FC<IPromoCarousel> = ({ slice }) => {
  const { promoCarousel } = useData();
  const { isLoading, activesPreCartDiscounts } = useSpecialsContext();

  const activeAutoplay = promoCarousel?.autoplay ?? false;
  const delay = promoCarousel?.autoplay_delay ?? 2500;
  const waitForTransition = promoCarousel?.wait_for_transition ?? false;
  const disableOnInteraction =
    promoCarousel?.disable_autoplay_on_interaction ?? false;
  const speed = promoCarousel?.autoplay_speed ?? 1000;

  // slice props
  const background = slice?.primary.background_color;
  const paddingSection = slice?.primary.padding_section;
  const marginBottom = slice?.primary.bottom_spacing;
  const titleColor = slice?.primary.title_color;
  const seeAllHref = slice?.primary?.see_all_link;
  const seeMoreText = slice?.primary?.see_all_text;
  const seeAllTextColor = slice?.primary?.see_all_text_color;
  const activeControlColor = slice?.primary?.active_control_color;
  const disableControlColor = slice?.primary?.disable_control_color;
  const backgroundImage = slice?.primary?.background_image;
  const backgroundStyleFixed = slice?.primary?.background_style_fixed_cover;
  const minHeight = slice?.primary?.min_heigth;
  const title = slice?.primary?.title as string;

  const breakpoints = {
    1024: {
      spaceBetween: 16,
    },
  };

  if (!slice?.primary.active) {
    return null;
  }

  const activesCount = Object.keys(activesPreCartDiscounts)?.length;

  if (!isLoading && !activesCount) {
    return null;
  }

  const sectionTitle = title ?? promoCarousel?.title;
  const countText = `${activesCount} ${
    activesCount === 1 ? 'promotion' : 'promotions'
  } available`;
  const sectionAriaLabel = sectionTitle
    ? `Promotions carousel: ${sectionTitle}, ${countText}`
    : `Promotions carousel, ${countText}`;

  const carouselStyle = {
    ...(activeControlColor
      ? { '--promo-control-color': activeControlColor }
      : {}),
    ...(disableControlColor
      ? { '--promo-control-disabled-color': disableControlColor }
      : {}),
    ...(seeAllTextColor ? { '--promo-control-border': 'transparent' } : {}),
  } as CSSProperties;

  return (
    <section
      aria-label={sectionAriaLabel}
      className={classNames(
        styles.promo__carousel,
        getSlicePadding(paddingSection),
        {
          spacing_bottom: marginBottom,
          ['background__fixed_cover']: backgroundStyleFixed,
          ['background__contained']: !backgroundStyleFixed,
        },
      )}
      style={{
        background: background!,
        backgroundImage: backgroundImage?.url
          ? `url(${backgroundImage?.url})`
          : undefined,
        minHeight: `${minHeight || 0}vh`,
        ...carouselStyle,
      }}
    >
      <Slider
        activatedHover={false}
        activeAutoplay={activeAutoplay}
        ariaLabel={`${sectionTitle || 'Promotions'} carousel`}
        autoplay={{ delay, disableOnInteraction, waitForTransition }}
        breakpoints={breakpoints}
        centeredSlides={false}
        className={classNames(styles.specials, 'special__carousel')}
        freeMode
        loop={activeAutoplay && activesCount > 3}
        seeAllElement={
          <Link
            aria-label={`Go to ${seeMoreText ?? 'See all'}, ${activesCount} ${
              activesCount === 1 ? 'promotion' : 'promotions'
            } available`}
            className={styles.specials__see_all}
            href={seeAllHref ?? TEMPLATE_BASE_PATH.PROMOTIONS}
            style={{ color: seeAllTextColor! }}
          >
            {seeMoreText ?? 'SEE ALL'}
            <span aria-hidden="true">({activesCount})</span>
          </Link>
        }
        sliderHeading={
          <Heading
            className={sectionTitle ? styles.header : 'sr-only'}
            level={2}
            style={{ color: titleColor! }}
          >
            {sectionTitle ?? 'Promotions'}
            <span className="sr-only">
              {' '}
              - {activesCount} {activesCount === 1 ? 'promotion' : 'promotions'}{' '}
              available
            </span>
          </Heading>
        }
        spaceBetween={12}
        speed={speed}
      >
        {isLoading
          ? Array.from({ length: 15 }).map((_, index) => (
              <div className={styles.special__placeholder} key={index}>
                <Placeholder className={styles.special__placeholder_image} />
                <Placeholder className={styles.special__placeholder_title} />
                <Placeholder className={styles.special__placeholder_code} />
              </div>
            ))
          : (
              Object.values(
                activesPreCartDiscounts,
              ) as PromoCarouselDocumentDataCarouselItemsItemWithID[]
            )
              .slice(0, 15)
              .map((special, index) =>
                special.promoCardVariation === 'splitCard' ? (
                  <PromoCardSplit
                    className={styles.special_split}
                    key={index}
                    special={special}
                    specialUrl={getSpecialURL(special)}
                  />
                ) : (
                  <PromoCard
                    className={styles.special}
                    key={index}
                    special={special}
                    specialUrl={getSpecialURL(special)}
                  />
                ),
              )}
      </Slider>
    </section>
  );
};

export default PromoCarousel;
